// src/v3/ui/_common/components/ui/select.tsx
//
// Custom Select — replaces native <select> across the app.
//
// Why not native <select>: the OS renders the <option> dropdown with the
// platform theme (white background on most OSes). In dark mode the page sets
// color: white, which <option> inherits → white text on white background.
// CSS cannot reliably style native <option> elements cross-browser.
//
// This component renders a button trigger + a portal-based dropdown panel,
// giving full control over dark-mode colors via Tailwind color utilities.

import { ChevronDown } from "lucide-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "../../lib/utils";

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  ariaLabel?: string;
  className?: string;
  /** Render the trigger at a fixed height matching h-8 inputs. */
  size?: "default" | "sm";
  disabled?: boolean;
  /** Show a search input at the top of the dropdown to filter options. */
  searchable?: boolean;
}

const TRIGGER_HEIGHT = {
  default: "h-10",
  sm: "h-8",
} as const;

const TRIGGER_PADDING = {
  default: "px-3",
  sm: "px-2",
} as const;

export function Select({
  value,
  onChange,
  options,
  placeholder,
  ariaLabel,
  className,
  size = "default",
  disabled = false,
  searchable = false,
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelPos, setPanelPos] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const selectedLabel = useMemo(
    () => options.find((o) => o.value === value)?.label ?? placeholder ?? "",
    [options, value, placeholder],
  );

  const selectedIndex = useMemo(
    () => options.findIndex((o) => o.value === value),
    [options, value],
  );

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchQuery) return options;
    const q = searchQuery.toLowerCase();
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q),
    );
  }, [options, searchable, searchQuery]);

  const positionPanel = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const panelHeight = panelRef.current?.offsetHeight ?? 200;
    const spaceBelow = window.innerHeight - rect.bottom;
    const above = spaceBelow < panelHeight + 8 && rect.top > panelHeight + 8;
    setPanelPos({
      top: above ? rect.top - panelHeight - 4 : rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  useLayoutEffect(() => {
    if (open) positionPanel();
  }, [open, positionPanel]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener("resize", positionPanel);
    window.addEventListener("scroll", positionPanel, true);
    return () => {
      window.removeEventListener("resize", positionPanel);
      window.removeEventListener("scroll", positionPanel, true);
    };
  }, [open, positionPanel]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handlePointer = (e: PointerEvent) => {
      const target = e.target;
      if (!(target instanceof Node)) return;
      if (
        triggerRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      )
        return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", handlePointer);
    return () => document.removeEventListener("pointerdown", handlePointer);
  }, [open]);

  // Reset highlight and search when opening.
  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setHighlightIndex(selectedIndex >= 0 ? selectedIndex : 0);
    }
  }, [open, selectedIndex]);

  const selectOption = useCallback(
    (idx: number) => {
      const opt = filteredOptions[idx];
      if (!opt) return;
      onChange(opt.value);
      setOpen(false);
      setSearchQuery("");
      triggerRef.current?.focus();
    },
    [filteredOptions, onChange],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (!open) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    switch (e.key) {
      case "Escape":
        e.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
        break;
      case "ArrowDown":
        e.preventDefault();
        setHighlightIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : prev,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (highlightIndex >= 0) selectOption(highlightIndex);
        break;
      case "Tab":
        setOpen(false);
        break;
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        onKeyDown={handleKeyDown}
        className={cn(
          "flex w-full items-center justify-between gap-2 rounded-md border border-input bg-background text-sm text-foreground transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "hover:bg-accent",
          TRIGGER_HEIGHT[size],
          TRIGGER_PADDING[size],
          className,
        )}
      >
        <span
          className={cn(
            "truncate text-left",
            selectedIndex < 0 && !placeholder && "text-muted-foreground",
            selectedIndex < 0 && placeholder && "text-muted-foreground",
          )}
        >
          {selectedLabel}
        </span>
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-180",
          )}
        />
      </button>
      {open &&
        panelPos &&
        createPortal(
          <div
            ref={panelRef}
            role="listbox"
            aria-label={ariaLabel}
            style={{
              position: "fixed",
              top: panelPos.top,
              left: panelPos.left,
              width: panelPos.width,
              zIndex: 50,
            }}
            className="max-h-60 overflow-y-auto rounded-md border border-input bg-background py-1 shadow-md"
            onKeyDown={handleKeyDown}
          >
            {searchable && (
              <div className="sticky top-0 border-b bg-background p-2">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setHighlightIndex(0);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      e.preventDefault();
                      setSearchQuery("");
                    }
                  }}
                  placeholder={placeholder ?? "Search..."}
                  aria-label="Search options"
                  className="w-full rounded-md border bg-background px-2 py-1 text-sm"
                  autoFocus
                />
              </div>
            )}
            {filteredOptions.length === 0 && (
              <div className="px-3 py-2 text-sm text-muted-foreground">—</div>
            )}
            {filteredOptions.map((opt, idx) => (
              <SelectItem
                key={opt.value}
                label={opt.label}
                selected={opt.value === value}
                highlighted={idx === highlightIndex}
                onClick={() => selectOption(idx)}
                onPointerEnter={() => setHighlightIndex(idx)}
              />
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}

function SelectItem({
  label,
  selected,
  highlighted,
  onClick,
  onPointerEnter,
}: {
  label: ReactNode;
  selected: boolean;
  highlighted: boolean;
  onClick: () => void;
  onPointerEnter: () => void;
}) {
  return (
    <div
      role="option"
      aria-selected={selected}
      onClick={onClick}
      onPointerEnter={onPointerEnter}
      className={cn(
        "flex cursor-pointer items-center px-3 py-1.5 text-sm text-foreground",
        highlighted && "bg-accent text-accent-foreground",
        !highlighted && selected && "bg-accent/50",
      )}
    >
      <span className="truncate">{label}</span>
    </div>
  );
}
